// soccer_admin/src/pages/Maintenance.jsx
import React, { useState, useEffect } from 'react';
import { 
  CogIcon, ClockIcon, LanguageIcon, 
  CheckCircleIcon, ExclamationTriangleIcon, ArrowPathIcon 
} from '@heroicons/react/24/outline';
import API from '../services/api';

const Maintenance = () => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ isEnabled: false, endTime: null, messages: {} });
  const [durationHours, setDurationHours] = useState(1);
  const [messages, setMessages] = useState({ ar: '', en: '', fr: '', es: '' });

  const fetchStatus = async () => {
    try {
      const res = await API.get('/maintenance/status');
      setStatus(res.data);
      if (res.data.messages) {
        setMessages(res.data.messages);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleToggle = async () => {
    const confirmMsg = status.isEnabled 
      ? '⚠️ Disable maintenance mode? Users will be able to access the game again.'
      : '🔧 Enable maintenance mode? All users will see a maintenance screen.';
    if (!window.confirm(confirmMsg)) return;
    setLoading(true);
    try {
      await API.post('/admin/maintenance', {
        isEnabled: !status.isEnabled,
        durationHours: !status.isEnabled ? durationHours : 0,
        messageAr: messages.ar,
        messageEn: messages.en,
        messageFr: messages.fr,
        messageEs: messages.es
      });
      await fetchStatus();
      alert(status.isEnabled ? '✅ Maintenance mode disabled' : '✅ Maintenance mode enabled');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMessages = async () => {
    setLoading(true);
    try {
      await API.post('/admin/maintenance', {
        isEnabled: status.isEnabled,
        durationHours: status.isEnabled ? (status.endTime ? Math.ceil((new Date(status.endTime) - new Date()) / 3600000) : 0) : 0,
        messageAr: messages.ar,
        messageEn: messages.en,
        messageFr: messages.fr,
        messageEs: messages.es
      });
      alert('💾 Messages saved successfully');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const remainingTime = status.endTime ? Math.max(0, Math.ceil((new Date(status.endTime) - new Date()) / 1000)) : 0;
  const hoursLeft = Math.floor(remainingTime / 3600);
  const minutesLeft = Math.floor((remainingTime % 3600) / 60);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-xl bg-yellow-500/20">
            <CogIcon className="h-8 w-8 text-yellow-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
            Maintenance Mode
          </h1>
        </div>
        <p className="text-gray-400 text-sm ml-2">Control game accessibility during updates or server maintenance.</p>
      </div>

      {/* Status Card */}
      <div
        className={`rounded-2xl p-6 mb-6 border transition-all duration-300 ${
          status.isEnabled 
            ? 'bg-gradient-to-br from-red-900/30 to-red-800/10 border-red-500/50 shadow-red-500/10' 
            : 'bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700 shadow-lg'
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${status.isEnabled ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
              {status.isEnabled ? (
                <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
              ) : (
                <CheckCircleIcon className="h-6 w-6 text-green-400" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Status</h2>
              <p className="text-gray-400 text-sm">Maintenance mode is currently</p>
            </div>
          </div>
          <div className={`px-4 py-2 rounded-full text-sm font-bold text-center w-full sm:w-auto ${
            status.isEnabled 
              ? 'bg-red-600 text-white shadow-lg shadow-red-500/20' 
              : 'bg-green-600 text-white shadow-lg shadow-green-500/20'
          }`}>
            {status.isEnabled ? 'ACTIVE' : 'INACTIVE'}
          </div>
        </div>

        {status.isEnabled && status.endTime && (
          <div className="mb-4 p-4 bg-black/40 rounded-xl border border-yellow-500/30 flex items-center gap-3">
            <ClockIcon className="h-5 w-5 text-yellow-400" />
            <p className="text-yellow-300 text-sm font-mono">
              Auto-end in: <span className="font-bold text-lg">{hoursLeft}h {minutesLeft}m</span>
            </p>
          </div>
        )}

        {/* Controls grid - improved alignment */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 items-end">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Auto-end after (hours)</label>
            <div className="relative">
              <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
              <input
                type="number"
                min="0"
                step="0.5"
                value={durationHours}
                onChange={(e) => setDurationHours(parseFloat(e.target.value) || 0)}
                className="w-full bg-gray-800 text-white p-2.5 pl-10 rounded-xl border border-gray-700 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                disabled={status.isEnabled}
              />
              <p className="text-xs text-gray-500 mt-1">0 = no auto-end (manual disable only)</p>
            </div>
          </div>
          <div>
            <button
              onClick={handleToggle}
              disabled={loading}
              className={`w-full py-2.5 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2 ${
                status.isEnabled 
                  ? 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-600' 
                  : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg'
              } disabled:opacity-50`}
            >
              {loading ? (
                <ArrowPathIcon className="h-5 w-5 animate-spin" />
              ) : status.isEnabled ? (
                <>Disable</>
              ) : (
                <>Enable</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Messages Card */}
      <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl border border-gray-700 shadow-xl overflow-hidden">
        <div className="border-b border-gray-700 px-6 py-4 bg-gray-800/30">
          <div className="flex items-center gap-2">
            <LanguageIcon className="h-5 w-5 text-yellow-400" />
            <h2 className="text-xl font-bold text-white">Maintenance Messages</h2>
            <span className="text-xs text-gray-400 ml-2">(4 languages)</span>
          </div>
          <p className="text-gray-400 text-sm mt-1">These messages will be shown to users during maintenance. Leave empty for default message.</p>
        </div>

        <div className="p-6 space-y-5">
          {[
            { code: 'ar', label: '🇸🇦 Arabic', placeholder: 'نحن نعمل على تحسين اللعبة. سنعود قريباً.', dir: 'rtl' },
            { code: 'en', label: '🇬🇧 English', placeholder: 'We are improving the game. We\'ll be back soon.', dir: 'ltr' },
            { code: 'fr', label: '🇫🇷 French', placeholder: 'Nous améliorons le jeu. Nous reviendrons bientôt.', dir: 'ltr' },
            { code: 'es', label: '🇪🇸 Spanish', placeholder: 'Estamos mejorando el juego. Volveremos pronto.', dir: 'ltr' }
          ].map((lang) => (
            <div key={lang.code} className="group">
              <label className="block text-sm font-medium text-gray-300 mb-1">{lang.label}</label>
              <textarea
                value={messages[lang.code]}
                onChange={(e) => setMessages({...messages, [lang.code]: e.target.value})}
                rows="2"
                dir={lang.dir}
                className="w-full bg-gray-800 text-white p-3 rounded-xl border border-gray-700 focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none transition"
                placeholder={lang.placeholder}
              />
            </div>
          ))}
        </div>

        <div className="border-t border-gray-700 px-6 py-4 bg-gray-800/30 flex justify-end">
          <button
            onClick={handleSaveMessages}
            disabled={loading}
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-6 py-2 rounded-xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? <ArrowPathIcon className="h-5 w-5 animate-spin" /> : <CheckCircleIcon className="h-5 w-5" />}
            Save Messages
          </button>
        </div>
      </div>

      {/* Helper info */}
      <div className="mt-8 text-center text-gray-500 text-xs">
        <p>ℹ️ When maintenance is enabled, all API requests (except status check) will be blocked with 503 error.</p>
        <p className="mt-1">Socket.IO connections will also be rejected during maintenance.</p>
      </div>
    </div>
  );
};

export default Maintenance;